import { useCallback, useEffect, useMemo, useState } from 'react'

import { useDebounce } from '@circlefin/hooks'
import { useModal } from '@circlefin/modal-router'
import { TravelRuleAlert } from '@features/blockchain/components'
import { useTravelRuleRequired } from '@features/users/hooks/travel-rule'
import { addressBookDetails } from '@services/sections/lib/address-book'
import { routes } from '@services/sections/modal/routes'
import { BLOCKCHAIN_ADDRESS_POLL_INTERVAL } from '@shared/apollo/pollInterval'
import { ComplexTable, TableCard } from '@shared/components/tables'
import {
  BlockchainAddressStatus,
  BlockchainRecipientAddressOwner,
  RecipientAddressFilterBy,
  useBlockchainRecipientAddressesWithMetadataQuery,
} from '@shared/graphql'
import classNames from 'classnames'
import { default as useIsMounted } from 'ismounted'
import { useRouter } from 'next/router'

import { PendingAddressesBanner } from '../../components'
import { PENDING_INFO_STATES } from '../../utils'

import { useAddressBookTableColumns } from './AddressBookTable.Columns'
import { AddressBookTableFilters } from './AddressBookTable.Filters'

import type { Row } from '@circlefin/components/lib/AdvancedTable'
import type {
  BlockchainAbbreviation,
  BlockchainRecipientAddressWithMetadata,
  Currency,
} from '@shared/graphql'

const { Body, Filters } = TableCard

export interface TableFilterProps {
  /**
   * Nickname.
   */
  nickname?: string
  /**
   * Blockchain.
   */
  blockchain?: BlockchainAbbreviation
  /**
   * Currency.
   */
  currency?: Currency
  /**
   * Status.
   */
  status?: BlockchainAddressStatus
}

export interface AddressBookTableProps {
  /**
   * Type of address to show based on an owner.
   */
  owner?: BlockchainRecipientAddressOwner
  /**
   * Enables table selection.
   */
  enableSelection?: boolean
  /**
   * Controls visibility of the action cell.
   */
  showActionCell?: boolean
  /**
   * Selected rows to initialize.
   */
  initialSelected?: BlockchainRecipientAddressWithMetadata[]
  /**
   * Will be called when the user selects addresses from the table.
   */
  onRowSelect?: (params: {
    owner: BlockchainRecipientAddressOwner
    addresses: BlockchainRecipientAddressWithMetadata[]
  }) => void
}

/**
 * Helper function to format filters for the recipient addresses with metadata query.
 * We combine PENDING_ADDITIONAL_INFORMATION and PENDING_BENEFICIARY_INFORMATION into one option in the status filter.
 * If the status is one of these, we need to query both statuses.
 */
const getRecipientAddressesWithMetadataFilters = (
  filters: TableFilterProps,
) => {
  const status = (() => {
    if (filters.status != null) {
      return PENDING_INFO_STATES.includes(filters.status)
        ? [
            BlockchainAddressStatus.PENDING_ADDITIONAL_INFORMATION,
            BlockchainAddressStatus.PENDING_BENEFICIARY_INFORMATION,
          ]
        : [filters.status]
    }
  })()

  return {
    descriptionFilter: filters.nickname,
    chain: filters.blockchain != null ? [filters.blockchain] : undefined,
    currency: filters.currency != null ? filters.currency : undefined,
    status,
  }
}

export const AddressBookTable: React.FC<AddressBookTableProps> = ({
  owner = BlockchainRecipientAddressOwner.customer,
  enableSelection = false,
  showActionCell = true,
  initialSelected,
  onRowSelect,
}) => {
  const columns = useAddressBookTableColumns({
    showActionCell,
  })

  const router = useRouter()
  const isMounted = useIsMounted()
  const { travelRuleSingaporeRequired, travelRuleRequired } =
    useTravelRuleRequired()

  const modal = useModal()
  const proposalId = router.query?.proposalId

  /**
   * Administrators will receive emails requesting they authorize new address book adds/removes.
   * A link in this email will take them to this page w/ a query param indicating the proposalId
   * which will open a modal that renders the proposed address book action and buttons to let them
   * approve / reject the proposal.
   */
  const showAddressBookApprovalModal = useCallback(async () => {
    await router.replace(
      {
        pathname: router.pathname,
        query: '',
      },
      undefined,
      { shallow: true },
    )
    modal.router.push({
      pathname: routes.account.addressBook.approvalRequest,
      query: {
        proposalId,
      },
    })
  }, [modal.router, proposalId, router])

  useEffect(() => {
    if (proposalId === undefined) return

    void showAddressBookApprovalModal()
  }, [proposalId, showAddressBookApprovalModal])

  const [currentTab, setTab] = useState(owner)
  const [filters, setFilters] = useState<TableFilterProps>({})

  /**
   * When the status of a blockchain address is `pending_verification`
   * we expect user to verify the address by sending a small amount from the added address
   * once the correct amount is received, backend will update the address status to `active`
   * we should keep polling the query so the table reflects the most updated address status.
   */
  const { data, called, loading, error, refetch, stopPolling } =
    useBlockchainRecipientAddressesWithMetadataQuery({
      notifyOnNetworkStatusChange: true,
      pollInterval: BLOCKCHAIN_ADDRESS_POLL_INTERVAL,
      fetchPolicy: 'cache-and-network',
      onCompleted: (data) => {
        // check if there is at least one address with `pending_verification` status, if no, stop polling
        const hasPendingAddress =
          data.blockchainRecipientAddressesWithMetadata.some(
            (address) =>
              address.status === BlockchainAddressStatus.PENDING_VERIFICATION,
          )
        if (!hasPendingAddress) {
          stopPolling()
        }
      },
      variables: {
        filter: {
          owner: currentTab,
          show: RecipientAddressFilterBy.ALL_ADDRESSES,
          ...getRecipientAddressesWithMetadataFilters(filters),
        },
      },
    })

  const debouncedFetchData = useDebounce({
    callback: (
      owner: BlockchainRecipientAddressOwner,
      filters: TableFilterProps,
    ) => {
      // we only want to invoke this query, when the component is still mounted
      if (isMounted.current) {
        void refetch({
          filter: {
            owner,
            show: RecipientAddressFilterBy.ALL_ADDRESSES,
            ...getRecipientAddressesWithMetadataFilters(filters),
          },
        })
      }
    },
    delay: 300,
  })

  const onChangeTab = useCallback(
    (newTab: BlockchainRecipientAddressOwner) => {
      // Clear table filters on tab change
      setFilters({})
      setTab(newTab)

      debouncedFetchData(newTab, {})
    },
    [debouncedFetchData],
  )

  const onRowClick = useCallback(
    ({ original }: Row<BlockchainRecipientAddressWithMetadata>) => {
      void router.push({
        pathname: addressBookDetails.route,
        query: {
          id: original.id,
        },
      })
    },
    [router],
  )

  /**
   * Getter to help Complex-table uniquely identify an address row object.
   */
  const getRowId = useCallback(
    (row: BlockchainRecipientAddressWithMetadata) => row.id,
    [],
  )

  const initialSelectedAddresses: Record<string, boolean> = useMemo(
    () =>
      (initialSelected ?? []).reduce(
        (prev, curr) => ({
          ...prev,
          [getRowId(curr)]: true,
        }),
        {},
      ),
    [getRowId, initialSelected],
  )

  const handleRowSelect = useCallback(
    (rows: Array<Row<BlockchainRecipientAddressWithMetadata>>) => {
      onRowSelect?.({
        addresses: rows.map((row) => row.original),
        owner: currentTab,
      })
    },
    [onRowSelect, currentTab],
  )

  const handleFilterChange = useCallback(
    (filters: TableFilterProps) => {
      setFilters(filters)

      debouncedFetchData(currentTab, filters)
    },
    [currentTab, debouncedFetchData],
  )

  return (
    <>
      <PendingAddressesBanner />
      <TableCard paddingVariant="none" disableCardAppearance>
        <Filters
          className={classNames('gap-x-2', {
            'grid-rows-2 grid-rows-': !travelRuleSingaporeRequired,
          })}
          onChange={handleFilterChange}
          size={12}
        >
          <AddressBookTableFilters
            currentTab={currentTab}
            onTabChange={onChangeTab}
          />
        </Filters>

        <Body>
          <ComplexTable<
            BlockchainRecipientAddressWithMetadata,
            TableFilterProps
          >
            columns={columns}
            data={data?.blockchainRecipientAddressesWithMetadata}
            enableRowSelection={enableSelection}
            enableSorting={false}
            error={error}
            getRowId={getRowId}
            initialState={{
              rowSelection: initialSelectedAddresses,
            }}
            loading={!called || (!data && loading)}
            onRowClick={!enableSelection ? onRowClick : undefined}
            onSelectedRowsChange={handleRowSelect}
            pagination="default"
            retry={refetch}
            selectionType="page"
            manualFiltering
          />
        </Body>
      </TableCard>
      <TravelRuleAlert
        className="mt-3"
        visible={
          travelRuleRequired &&
          data?.blockchainRecipientAddressesWithMetadata.length === 0
        }
      />
    </>
  )
}
